import { api, readTotalCount } from '../api/client';
import { API_COLLECTIONS_URL, API_PACKAGES_URL } from '../utils';
import { postAndFollow } from './packageInstallFollow';
// A package's page, bare or `<publisher>/<name>` (keepr-api KPR-358).
import { packagePath } from '../utils/packageAddress';

// Discover packages (keepr-api docs/SCHEMA.md "Discover packages"): the
// published catalog, one package's detail, the install, and the demo-item
// sweep. Errors propagate: every caller shows its own words for a refusal.

/** `{ rows, total }` for the published catalog, filtered by `q` (KQL, packages subject). */
export async function listPackages(q) {
	const { data, headers } = await api.get(API_PACKAGES_URL, { params: q ? { q } : {} });
	const rows = Array.isArray(data) ? data : [];
	return { rows, total: readTotalCount(headers, rows.length) };
}

export async function getPackage(key) {
	const { data } = await api.get(`${API_PACKAGES_URL}/${encodeURIComponent(key)}`);
	return data;
}

/**
 * `target`: `{ collectionId }` or `{ newCollection: { name } }`; `items`:
 * 'none' | 'included'. Resolves to `{ collectionId, createdCollection,
 * cardIds, elementSetIds, itemCount, tagIds, rules, automations,
 * notifications, warnings, settings?, keyAdjusted, addOns? }` — `rules` the
 * automatic tags it made, each `{ tagId, name, enabled: false, preview }`
 * (keepr-api tags T10: they arrive paused); `automations` and
 * `notifications` what it brought, each `{ id, name, enabled: false,
 * awaitingPerson: true, path }` (keepr-api KPR-419 / KPR-361: every one
 * arrives paused; `path` the page whose Waiting for you previews it and
 * turns it on); `settings` what a card or bundle package added to the
 * collection, `{ added, leftOut, overview }` (keepr-api KPR-338: added,
 * never over what is there — see installedSettingsNote); `keyAdjusted` the
 * keys it suffixed (keepr-api KPR-357, keyAdjustedNote); `addOns`, for a
 * bundle with add-ons, `{ installed, notInstalled }` (keepr-api KPR-383).
 * A long install is followed to the end (KPR-1655, packageInstallFollow.js).
 */
export async function installPackage(key, { target, items = 'none', addOns, listing, healthDataConsent, bindings }, { onProgress } = {}) {
	// A bundle's add-ons (keepr-api KPR-383): sent only when the package has
	// some, so a package without any posts exactly what it always did. The
	// listing it was opened from (keepr-api KPR-465) brings that listing's
	// examples; sent only when there is one. The health-data consent version
	// the person agreed to (keepr-api KPR-478): sent only for a health package.
	const body = { target, items, ...(Array.isArray(addOns) ? { addOns } : {}), ...(listing ? { listing } : {}), ...(healthDataConsent ? { healthDataConsent } : {}), ...(bindings ? { bindings } : {}) };
	return postAndFollow(key, `${API_PACKAGES_URL}/${encodeURIComponent(key)}/install`, body, onProgress);
}

/**
 * What `installPackage` would do into the same target, written nothing
 * (keepr-api KPR-445, POST …/install/preview): `{ installable, refusal,
 * target, keyAdjusted, settings?, warnings, consent }`. `refusal` is the
 * install's own 409 (`package_key_taken`, `package_already_installed`,
 * `package_unavailable`, `package_outdated`) as data, not an error;
 * `keyAdjusted` and `settings` (`{ added, leftOut, overview }`) are the
 * install answer's, in its words; `consent.asked` says the install will ask
 * the person to agree first (a health package) — the dialog does not show it,
 * since its consent step (KPR-478) comes before the form the preview sits in,
 * and a preview needs no consent to be asked. The body is the install's,
 * less `items` (examples never clash with anything); errors propagate, and the
 * dialog treats a failed preview as "no preview", never as a reason not to add.
 */
export async function previewPackageInstall(key, { target, addOns, listing, items, bindings }) {
	const body = { target, ...(items === 'included' ? { items } : {}), ...(Array.isArray(addOns) ? { addOns } : {}), ...(listing ? { listing } : {}), ...(bindings ? { bindings } : {}) };
	const { data } = await api.post(`${API_PACKAGES_URL}/${encodeURIComponent(key)}/install/preview`, body);
	return data;
}

/**
 * What of a bundle a collection holds (keepr-api KPR-383; manage on the
 * collection): `{ collectionId, installed, version, addOns: [{ key, name,
 * description, default, needs, installed, items? }] }` — `items` how many
 * examples the add-on would bring there (keepr-api KPR-465). An API without add-ons
 * answers 404 — the caller treats that as "not known" and keeps the install.
 */
export async function getPackageAddOns(key, collectionId) {
	const { data } = await api.get(`${API_PACKAGES_URL}/${encodeURIComponent(key)}/add-ons`, { params: { collectionId } });
	return data;
}

/**
 * "Add an add-on" to a collection that holds the bundle (keepr-api KPR-383).
 * Resolves to the install's answer for what it made, with `addOns: {
 * installed, alreadyInstalled, notInstalled }`. `items: 'included'` brings
 * the add-ons' own examples (keepr-api KPR-465); sent only then, so a call
 * without examples posts what it always did.
 */
export async function addPackageAddOns(key, { collectionId, addOns, items, healthDataConsent, bindings }, { onProgress } = {}) {
	return postAndFollow(key, `${API_PACKAGES_URL}/${encodeURIComponent(key)}/add-ons`, { collectionId, addOns, ...(items === 'included' ? { items } : {}), ...(healthDataConsent ? { healthDataConsent } : {}), ...(bindings ? { bindings } : {}) }, onProgress);
}

/**
 * What `addPackageAddOns` would do with the same body, written nothing
 * (keepr-api KPR-632, POST …/add-ons/preview): `{ installable, refusal,
 * target, consent, keyAdjusted, cards, elementSets, addOns, core, settings,
 * examples?, warnings }` — `refusal` the call's own refusal as data
 * (`package_not_installed`, `addon_already_installed`, `addon_needs`,
 * `addon_core_missing`, `package_key_taken`, …), `core` what it would put on
 * the cards, dashboards and forms already there and what it would leave off,
 * `examples` (asked with `items: 'included'`) how many it would bring. Errors
 * propagate; the dialog treats a failed preview as "no preview", never as a
 * reason not to add.
 */
export async function previewPackageAddOns(key, { collectionId, addOns, items, bindings }) {
	const { data } = await api.post(`${API_PACKAGES_URL}/${encodeURIComponent(key)}/add-ons/preview`, { collectionId, addOns, ...(items === 'included' ? { items } : {}), ...(bindings ? { bindings } : {}) });
	return data;
}

// ---- health packages ask for consent (keepr-api KPR-478) ----

/**
 * The consent a package's install needs, or null: `{ version, heading,
 * points, agree, page, pageLabel }`. The API decides which packages are health
 * packages (their Discover category is Health) and serves the words; the
 * client never keeps its own list, so a package filed under Health tomorrow
 * asks without a web release.
 */
export function healthConsentOf(pkg) {
	const consent = pkg?.healthData?.consent;
	if (!consent || typeof consent.version !== 'string' || !Array.isArray(consent.points)) return null;
	// The page link is an in-app path and nothing else: a served value that is
	// not one (`javascript:`, `//elsewhere`) is dropped, never put in an href.
	const page = typeof consent.page === 'string' && /^\/(?!\/)/.test(consent.page) ? consent.page : null;
	return { ...consent, page };
}

/** The two refusals that mean "ask again", with the text to ask with: `{ consent, changed }` or null. */
export function consentRefusal(error) {
	const data = error?.response?.data;
	const code = data?.code;
	if (code !== 'health_data_consent_required' && code !== 'health_data_consent_outdated') return null;
	const consent = healthConsentOf({ healthData: { consent: data.consent } });
	return consent ? { consent, changed: code === 'health_data_consent_outdated' } : null;
}

// ---- a package's examples (keepr-api KPR-465): src/utils/packageExampleCounts.js ----
export { addLaterExampleCount, exampleCountFor } from '../utils/packageExampleCounts';

// ---- a bundle's add-ons (keepr-api KPR-383) ----

const addOnsOf = pkg => (Array.isArray(pkg?.addOns) ? pkg.addOns : []);
const byKeyOf = addOns => new Map(addOns.map(a => [a.key, a]));

/** `keys` and every add-on they need, transitively, in the package's order. */
export function withNeeds(addOns, keys) {
	const byKey = byKeyOf(addOns);
	const out = new Set();
	const add = (key) => {
		if (out.has(key) || !byKey.has(key)) return;
		out.add(key);
		(byKey.get(key).needs || []).forEach(add);
	};
	(keys || []).forEach(add);
	return addOns.map(a => a.key).filter(k => out.has(k));
}

/** The add-ons that need `key`, transitively (those that cannot stay ticked without it). */
function dependentsOf(addOns, key) {
	const out = new Set();
	const reaches = a => (a.needs || []).some(need => need === key || out.has(need));
	for (let grew = true; grew;) {
		const more = addOns.filter(a => !out.has(a.key) && reaches(a));
		more.forEach(a => out.add(a.key));
		grew = more.length > 0;
	}
	return addOns.map(a => a.key).filter(k => out.has(k));
}

/**
 * What the install dialog ticks first: the listing's add-ons, or the ones the
 * package marks `default` — each with what it needs, so the choice is always
 * one the server takes.
 */
export function presetAddOns(pkg, listingKey) {
	const addOns = addOnsOf(pkg);
	const listing = (pkg?.listings || []).find(l => l.key === listingKey);
	const keys = listing ? (listing.addOns || []) : addOns.filter(a => a.default).map(a => a.key);
	return withNeeds(addOns, keys);
}

/**
 * One checkbox changed. Ticking an add-on ticks what it needs; unticking one
 * that a ticked add-on needs unticks that one too. → `{ selected, also }`:
 * the new selection (package order) and the OTHER add-ons the change moved,
 * for the dialog to say so.
 */
export function toggleAddOn(addOns, selected, key, checked) {
	const now = new Set(selected);
	if (checked) {
		const next = withNeeds(addOns, [...now, key]);
		return { selected: next, also: next.filter(k => k !== key && !now.has(k)) };
	}
	const also = dependentsOf(addOns, key).filter(k => now.has(k));
	const drop = new Set([key, ...also]);
	return { selected: addOns.map(a => a.key).filter(k => now.has(k) && !drop.has(k)), also };
}

/** The dialog's word after a toggle moved other add-ons too ('' when it moved none). */
export function addOnToggleNote(addOns, key, checked, also) {
	if (!also || !also.length) return '';
	const byKey = byKeyOf(addOns);
	const name = k => byKey.get(k)?.name || k;
	const others = inWords(also.map(name));
	if (checked) return `Also ticked ${others}: ${name(key)} needs ${also.length === 1 ? 'it' : 'them'}.`;
	return `Unticked ${others} too: ${also.length === 1 ? 'it needs' : 'they need'} ${name(key)}.`;
}

/**
 * The tiles a package shows on Discover: one per listing preset, each linking
 * to the package page with that listing (`?listing=`), or the package's own
 * when it has none.
 */
export function discoverTiles(pkg) {
	const listings = Array.isArray(pkg?.listings) ? pkg.listings : [];
	if (!listings.length) return [{ id: pkg.key, to: packagePath(pkg.key), pkg, listing: null }];
	return listings.map(listing => ({ id: `${pkg.key}:${listing.key}`, to: packagePath(pkg.key, { listing: listing.key }), pkg, listing }));
}

/** A listing tile's line: its cards, and its add-ons ticked of those offered. */
export function listingContentsLine(pkg, listing) {
	const parts = [];
	const cards = listing?.cardCount || 0;
	if (cards) parts.push(cards === 1 ? '1 card' : `${cards} cards`);
	const offered = addOnsOf(pkg).length;
	const ticked = (listing?.addOns || []).length;
	if (offered) parts.push(`${ticked} of ${offered} add-on${offered === 1 ? '' : 's'}`);
	// Not its examples (keepr-api KPR-465): at 375 px the tile's one line,
	// after "Published by keepr", was already cut there ("10 ex…"). The
	// package page counts them.
	return parts.join(' · ');
}

// keepr-api KPR-1330: a whole setting that waits on several add-ons, by the
// kind the build report says — in the product's words, one and many.
const WHOLE_SETTING_WORDS = {
	savedFilter: ['filter', 'filters'],
	chart: ['chart', 'charts'],
	dashboard: ['dashboard', 'dashboards'],
	automation: ['rule', 'rules'],
	notification: ['notification', 'notifications'],
	quickAddTemplate: ['quick add', 'quick adds'],
	multiAddForm: ['multi add', 'multi adds'],
};

/**
 * A whole setting that waits on several add-ons (keepr-api KPR-1330: `{ kind:
 * 'setting', setting, name, card?, layout?, layoutName? }`) in words, never
 * its raw kind: "the "Trailer defects" filter", "the Trailer table", "the
 * "Fill check" page", "the "Inspection logs a fill" rule".
 */
export function wholeSettingWords(h) {
	if (!h) return '';
	if (h.setting === 'cardLayout') {
		if (h.layout === 'page') return `the "${h.layoutName || h.name}" page`;
		const kind = h.layout === 'tile' ? 'tile' : h.layout === 'table' ? 'table' : h.layout === 'form' ? 'form' : 'layout';
		return h.card ? `the ${h.card} ${kind}` : `a ${kind}`;
	}
	if (h.setting === 'cardEntry') return `the ${h.card || h.name} card's favorite and item-tag settings`;
	const words = WHOLE_SETTING_WORDS[h.setting];
	return words ? `the "${h.name}" ${words[0]}` : `"${h.name}"`;
}

/**
 * What an add-on adds to cards and dashboards it does not bring (keepr-api
 * KPR-457), in words: "Fills and Fuel spend to Vehicle and the Gallons chart
 * to Overview". `holders` `[{ kind: 'element', card, element, label } |
 * { kind: 'tile', dashboard, chart } | { kind: 'formPart', part: 'placed',
 * card, element, label }]` (KPR-1257: a field placed on a form, "Fill
 * odometer to the Trailer form"; KPR-1329: `{ kind: 'layoutPart', layout:
 * 'table' | 'tile' | 'page', part, card, layoutName?, element, label }`, "the
 * Trailer column to the Inspection table", "Fill odometer to the Vehicle
 * tile", "Fill odometer to the Fuel sheet page", "the sort by Fill odometer
 * to the Vehicle table"; KPR-1388: `{ kind: 'quickAddPart', quickAdd, part:
 * 'default' | 'lastItemMatch', element, label }` and `{ kind: 'multiAddPart',
 * multiAdd, part: 'step' | 'stepField', step, element?, label }`, "the
 * Odometer default to the Log service quick add", "the Fill-up step to the
 * Log a fill multi add"; KPR-1412: a page block naming only a card or set,
 * `named: 'card' | 'set'`, its name in `element`, "the Fill-up block to the
 * Service sheet page"; KPR-1389: a preset list section an add-on claims,
 * `{ kind: 'quickAddPart', part: 'presetSection', quickAdd, section, label }`,
 * "the Trailer section of Pre-trip inspection"; KPR-1330: `{ kind: 'setting', setting, name, card?,
 * layout?, layoutName? }`, a whole setting, "the "Trailer defects" filter";
 * KPR-1265: `{ kind: 'example', card, count }`, examples that wait on several
 * add-ons, counted by card, "1 Weigh ticket example", "2 Weigh ticket examples"),
 * names as the person reads them; at most `max`
 * things are named, the rest counted. A field a form leaves out is no "add":
 * holdersClause says it.
 */
export function holdersPhrase(holders, { max = 6 } = {}) {
	const list = Array.isArray(holders) ? holders.filter(Boolean) : [];
	const shown = list.slice(0, max);
	const groups = new Map();
	const targetOf = (h) => {
		if (h.kind === 'tile') return h.dashboard;
		if (h.kind === 'formPart') return `the ${h.card} form`;
		if (h.kind === 'layoutPart') return h.layout === 'page' ? `the ${h.layoutName || 'printed'} page` : `the ${h.card} ${h.layout === 'tile' ? 'tile' : 'table'}`;
		// keepr-api KPR-1389: a claimed preset list section is OF its quick add.
		if (h.kind === 'quickAddPart' && h.part === 'presetSection') return h.quickAdd;
		// keepr-api KPR-1388: a quick add's default, a multi add's step or field.
		if (h.kind === 'quickAddPart') return `the ${h.quickAdd} quick add`;
		if (h.kind === 'multiAddPart') return `the ${h.multiAdd} multi add`;
		return h.card;
	};
	shown.forEach((h) => {
		// keepr-api KPR-1265: examples, counted by their card ("1 Weigh ticket
		// example", "2 Weigh ticket examples"), never one by name.
		if (h.kind === 'example') {
			const key = `example\u0000${h.card}`;
			if (!groups.has(key)) groups.set(key, { kind: 'example', target: h.card, count: 0, names: [] });
			groups.get(key).count += Number(h.count) || 1;
			return;
		}
		// keepr-api KPR-1330: a whole setting is said on its own ("the "Trailer
		// defects" and "Checked fills" filters"); a layout or page by itself.
		if (h.kind === 'setting') {
			const plural = WHOLE_SETTING_WORDS[h.setting];
			const key = plural ? `setting\u0000${h.setting}` : `setting\u0000${groups.size}`;
			if (!groups.has(key)) groups.set(key, { kind: 'setting', setting: h.setting, words: plural, names: [], one: wholeSettingWords(h) });
			groups.get(key).names.push(h.name);
			return;
		}
		const target = targetOf(h);
		// A table's columns are said as columns, its sort on its own.
		let kind = (h.kind === 'layoutPart' && (h.part === 'column' || h.part === 'sort')) || h.kind === 'quickAddPart' || (h.kind === 'multiAddPart' && h.part === 'step') ? h.part : h.kind;
		// keepr-api KPR-1412: a block naming only a card or set is that one's block.
		if (h.kind === 'layoutPart' && h.part === 'block' && h.named) kind = 'cardBlock';
		const key = `${kind}\u0000${target}`;
		if (!groups.has(key)) groups.set(key, { kind, target, names: [] });
		groups.get(key).names.push(h.kind === 'tile' ? h.chart : (h.section || h.label || h.element));
	});
	const parts = [...groups.values()].map((g) => {
		const many = g.names.length === 1 ? '' : 's';
		if (g.kind === 'setting') return g.words && g.names.length > 1 ? `the ${inWords(g.names.map(n => `"${n}"`))} ${g.words[1]}` : g.one;
		if (g.kind === 'example') return `${g.count} ${g.target ? `${g.target} ` : ''}example${g.count === 1 ? '' : 's'}`;
		if (g.kind === 'tile') return `the ${inWords(g.names)} chart${many} to ${g.target}`;
		if (g.kind === 'column') return `the ${inWords(g.names)} column${many} to ${g.target}`;
		if (g.kind === 'sort') return `the sort by ${inWords(g.names)} to ${g.target}`;
		if (g.kind === 'default') return `the ${inWords(g.names)} default${many} to ${g.target}`;
		if (g.kind === 'lastItemMatch') return `matching the last item on ${inWords(g.names)} to ${g.target}`;
		if (g.kind === 'step') return `the ${inWords(g.names)} step${many} to ${g.target}`;
		if (g.kind === 'presetSection') return `the ${inWords(g.names)} section${many} of ${g.target}`;
		if (g.kind === 'cardBlock') return `the ${inWords([...new Set(g.names)])} block${many} to ${g.target}`;
		return `${inWords(g.names)} to ${g.target}`;
	});
	const rest = list.length - shown.length;
	if (rest > 0) parts.push(`${rest} more`);
	return inWords(parts);
}

const leavesOut = h => h && h.kind === 'formPart' && h.part === 'leftOut';

/**
 * What waits on an add-on, as a verb phrase (keepr-api KPR-1257): what it adds
 * (holdersPhrase) and the fields it leaves out of a form — "adds Fills to
 * Vehicle and leaves Fill odometer and Fuel spend out of the Trailer form".
 * `past`: "added … left …". `elideAdds` drops a leading "adds" (a list that
 * already said it). '' when there is nothing.
 */
export function holdersClause(holders, { past = false, max = 6, elideAdds = false } = {}) {
	const list = Array.isArray(holders) ? holders.filter(Boolean) : [];
	const adds = list.filter(h => !leavesOut(h));
	const outs = list.filter(leavesOut);
	const said = [];
	if (adds.length) said.push(`${elideAdds ? '' : (past ? 'added ' : 'adds ')}${holdersPhrase(adds, { max })}`);
	if (outs.length) {
		const shown = outs.slice(0, max);
		const forms = new Map();
		shown.forEach((h) => {
			if (!forms.has(h.card)) forms.set(h.card, []);
			forms.get(h.card).push(h.label || h.element);
		});
		const parts = [...forms.entries()].map(([card, names]) => `${inWords(names)} out of the ${card} form`);
		if (outs.length > shown.length) parts.push(`${outs.length - shown.length} more`);
		said.push(`${past ? 'left' : 'leaves'} ${inWords(parts)}`);
	}
	return said.join(' and ');
}

/**
 * After an install or "Add an add-on" (keepr-api KPR-383): which add-ons were
 * added, and which can still be added later — and (KPR-457) what an add-on
 * added later put on the cards and dashboards you have, and what each add-on
 * not here would also add to them. '' when the package has none. What it left
 * out of yours is a warning of its own, with its reason.
 */
export function addOnsInstalledNote(answer) {
	if (!answer) return '';
	const names = list => (Array.isArray(list) ? list.map(a => a.name) : []);
	const added = names(answer.installed);
	const later = names(answer.notInstalled);
	const parts = [];
	if (added.length) parts.push(`Added ${inWords(added)}.`);
	const placed = answer.core && Array.isArray(answer.core.added) ? answer.core.added : [];
	if (placed.length) parts.push(`It also ${holdersClause(placed, { past: true })}.`);
	if (later.length) parts.push(`${inWords(later)} ${later.length === 1 ? 'is' : 'are'} not added; you can add ${later.length === 1 ? 'it' : 'them'} later from the package's page.`);
	const waiting = Array.isArray(answer.waiting) ? answer.waiting.filter(w => w && Array.isArray(w.holders) && w.holders.length) : [];
	waiting.slice(0, 3).forEach(w => parts.push(`Adding ${w.name} also ${holdersClause(w.holders)}.`));
	return parts.join(' ');
}

/**
 * A package page's add-on (keepr-api KPR-457): what it adds to the cards and
 * dashboards it does not bring, from the detail's `adds` (local ids), named
 * by the package's own cards and dashboards. '' when it adds none. An element
 * that waits on this add-on AND others (keepr-api KPR-1256, `with` their
 * keys) is said with them: "Also adds Fills to Vehicle, and Trailer to
 * Inspection once Trailers is added too". `addOnByKey` names those add-ons.
 * A field a card's form leaves out (KPR-1257) is said as such: "…, and
 * leaves Fill odometer out of the Trailer form once Trailers is added too".
 * Examples that wait on several add-ons (KPR-1265) are counted by card:
 * "Also adds 2 Weigh ticket examples once Trailers is added too".
 */
export function addOnAddsLine(addOn, { cardsByLocal = new Map(), dashboardsByLocal = new Map(), addOnByKey = new Map() } = {}) {
	const adds = Array.isArray(addOn?.adds) ? addOn.adds : [];
	if (!adds.length) return '';
	const holderOf = (a) => {
		if (a.kind === 'tile') return { kind: 'tile', dashboard: dashboardsByLocal.get(a.dashboard)?.name || 'a dashboard', chart: a.chart };
		// KPR-1257: a field a card's form places or leaves out, once this add-on is here.
		if (a.kind === 'formPart') return { kind: 'formPart', part: a.part, card: cardsByLocal.get(a.card)?.name || 'a card', element: a.element, label: a.label };
		// KPR-1329: a table's column or sort, a tile's field, a page's block.
		if (a.kind === 'layoutPart') return { kind: 'layoutPart', layout: a.layout, part: a.part, card: cardsByLocal.get(a.card)?.name || 'a card', layoutName: a.layoutName, element: a.element, ...(a.named ? { named: a.named } : {}), label: a.label };
		// KPR-1388: a quick add's default, a multi add's step or field.
		if (a.kind === 'quickAddPart') return { kind: 'quickAddPart', part: a.part, quickAdd: a.quickAdd, element: a.element, ...(a.section ? { section: a.section } : {}), label: a.label };
		if (a.kind === 'multiAddPart') return { kind: 'multiAddPart', part: a.part, multiAdd: a.multiAdd, step: a.step, element: a.element, label: a.label };
		// KPR-1265: examples once their add-ons are all here, counted by card.
		if (a.kind === 'example') return { kind: 'example', card: cardsByLocal.get(a.card)?.name || '', count: a.count };
		// KPR-1330: a whole setting (a filter, chart, rule, a page…) once its add-ons are all here.
		if (a.kind === 'setting') return { kind: 'setting', setting: a.setting, name: a.name, card: a.card ? cardsByLocal.get(a.card)?.name || 'a card' : undefined, layout: a.layout, layoutName: a.layoutName };
		return { kind: 'element', card: cardsByLocal.get(a.card)?.name || 'a card', element: a.element, label: a.label };
	};
	const own = adds.filter(a => !(Array.isArray(a.with) && a.with.length));
	const joint = new Map();
	adds.filter(a => Array.isArray(a.with) && a.with.length).forEach((a) => {
		const key = a.with.join('+');
		if (!joint.has(key)) joint.set(key, { names: a.with.map(k => addOnByKey.get(k)?.name || k), holders: [] });
		joint.get(key).holders.push(holderOf(a));
	});
	const groups = [
		...(own.length ? [{ holders: own.map(holderOf), once: '' }] : []),
		...[...joint.values()].map(j => ({ holders: j.holders, once: ` once ${inWords(j.names)} ${j.names.length === 1 ? 'is' : 'are'} added too` })),
	];
	// "adds" said once, by the first group that adds anything.
	let said = false;
	const parts = groups.map((g) => {
		const adds = g.holders.some(h => !leavesOut(h));
		const clause = holdersClause(g.holders, { elideAdds: said && adds });
		if (adds) said = true;
		return `${clause}${g.once}`;
	});
	return `Also ${parts.length > 1 ? `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}` : parts[0]}`;
}

/**
 * The add-ons an element on a package card comes with — or, keepr-api
 * KPR-1330, a chart or dashboard that waits whole on several (keepr-api KPR-457:
 * `addOn`; KPR-1256: `with`, when it waits on several), named: "Inspections
 * and Trailers". '' when it is the core's.
 */
export function elementAddOnsWords(el, addOnByKey = new Map()) {
	if (!el?.addOn) return '';
	return inWords([el.addOn, ...(Array.isArray(el.with) ? el.with : [])].map(k => addOnByKey.get(k)?.name || k));
}

const demoItemsUrl = collectionId => `${API_COLLECTIONS_URL}/${encodeURIComponent(collectionId)}/demo-items`;

/**
 * What removing the demo data would do (GET …/demo-items/preview; manage,
 * session-only; writes nothing; keepr-api KPR-363): `{ collectionId, examples:
 * { count, kept }, links: { records, hidden, named: [{ id, title, cardId }] },
 * previewToken }`. `links` is the person's OWN records that hold a link to an
 * example about to go: `records` counts them all, `named` is the first few
 * (only those the caller can read), `hidden` the ones they cannot.
 */
export async function previewDemoItems(collectionId) {
	const { data } = await api.get(`${demoItemsUrl(collectionId)}/preview`);
	return data;
}

/**
 * Removes the items a package added as demo data (DELETE …/demo-items) →
 * `{ deleted, kept, links: { records, hidden } }`. Send the preview's token
 * and the API refuses (409 `demo_preview_stale`) when the list moved since
 * the person looked. A failure part-way carries `partial: { deleted, left,
 * kept, failed }` in the response body.
 */
export async function deleteDemoItems(collectionId, previewToken) {
	const { data } = await api.delete(demoItemsUrl(collectionId), previewToken ? { params: { previewToken } } : undefined);
	return data;
}

// What a package holds, in words: "4 cards · 12 demo items · 5 tags".
export function packageContentsLine(pkg) {
	const contents = pkg?.contents || {};
	const parts = [];
	const cards = contents.cards || 0;
	if (cards) parts.push(cards === 1 ? '1 card' : `${cards} cards`);
	const items = contents.items || 0;
	if (items) {
		const noun = contents.itemsRole === 'demo' ? 'demo item' : 'item';
		parts.push(items === 1 ? `1 ${noun}` : `${items} ${noun}s`);
	}
	// A collection package's tags (keepr-api tags T10).
	const tags = contents.tags || 0;
	if (tags) parts.push(tags === 1 ? '1 tag' : `${tags} tags`);
	// Its charts and dashboards for everyone (keepr-api charts A10).
	const charts = contents.charts || 0;
	if (charts) parts.push(charts === 1 ? '1 chart' : `${charts} charts`);
	const dashboards = contents.dashboards || 0;
	if (dashboards) parts.push(dashboards === 1 ? '1 dashboard' : `${dashboards} dashboards`);
	// Its automations and notifications (keepr-api KPR-419 / KPR-361), each paused.
	const automations = contents.automations || 0;
	if (automations) parts.push(automations === 1 ? '1 automation' : `${automations} automations`);
	const notifications = contents.notifications || 0;
	if (notifications) parts.push(notifications === 1 ? '1 notification' : `${notifications} notifications`);
	// The forms it prepares, each arriving off (keepr-api KPR-360).
	const forms = contents.forms || 0;
	if (forms) parts.push(forms === 1 ? '1 form' : `${forms} forms`);
	return parts.join(' · ');
}

/**
 * The words for a package's forms (keepr-api KPR-360, ruling a15): every one
 * arrives PREPARED AND OFF — no link, no address, the collection's intake
 * switch as it was — and nobody can use one until a person reads what it
 * exposes and makes it live. `count` forms → one sentence, or '' for none.
 */
export function preparedFormsNote(count) {
	if (!count) return '';
	return count === 1
		? 'Its form arrives prepared and off: nobody can use it until you read what it asks and make it live.'
		: `Its ${count} forms arrive prepared and off: nobody can use one until you read what it asks and make it live.`;
}

/** A prepared form's kind in words (keepr-api KPR-360): `public` or `invite`. */
export const formKindWords = kind => (kind === 'public' ? 'public form' : 'invite link');

/**
 * The words for a package's automatic tags (keepr-api tags T10): every rule a
 * package brings arrives PAUSED, and tags nothing until the person resumes it.
 * `count` rules → one sentence, or '' for none.
 */
export function pausedRulesNote(count) {
	if (!count) return '';
	return count === 1
		? 'Its automatic tag arrives paused: nothing is tagged until you resume its rule in Settings › Tags.'
		: `Its ${count} automatic tags arrive paused: nothing is tagged until you resume each rule in Settings › Tags.`;
}

/**
 * The words for a package's automations and notifications (keepr-api KPR-419
 * / KPR-361, ruling d30): every one arrives PAUSED, and nothing runs or is
 * sent until the person turns each on — in Settings › Automations or
 * Notifications, where Waiting for you shows what it would do first.
 * `{ automations, notifications }` counts → one sentence, or '' for none.
 */
export function pausedAutomationsNote({ automations = 0, notifications = 0 } = {}) {
	if (!automations && !notifications) return '';
	const count = (n, one) => (n === 1 ? `its ${one}` : `its ${n} ${one}s`);
	const what = [automations ? count(automations, 'automation') : null, notifications ? count(notifications, 'notification') : null].filter(Boolean).join(' and ');
	const one = automations + notifications === 1;
	const doing = automations && notifications ? 'runs or is sent' : (automations ? 'runs' : 'is sent');
	const where = [automations ? 'Automations' : null, notifications ? 'Notifications' : null].filter(Boolean).join(' and ');
	const head = what.charAt(0).toUpperCase() + what.slice(1);
	return `${head} ${one ? 'arrives' : 'arrive'} paused: nothing ${doing} until you turn ${one ? 'it' : 'each'} on, after ${one ? 'its' : 'their'} preview, in Settings › ${where}.`;
}

// A list in words: "a, b and c".
const inWords = parts => (parts.length < 2 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`);

/**
 * What a card or bundle package adds to the collection it goes into
 * (keepr-api KPR-338; keepr-web docs/plans/pack-settings-merge.md): its own
 * cards' saved filters, quick adds, multi add forms, layouts, charts and
 * dashboards — ADDED, never over what is there. One sentence from the
 * listing's `contents`, or '' when it carries none.
 */
export function packSettingsNote(contents) {
	const c = contents || {};
	const parts = [];
	const count = (n, one, many) => { if (n) parts.push(n === 1 ? `a ${one}` : `${n} ${many}`); };
	count(c.savedFilters, 'saved filter', 'saved filters');
	count(c.quickAddTemplates, 'quick add', 'quick adds');
	count(c.multiAddForms, 'multi add form', 'multi add forms');
	count(c.cardLayouts, 'layout', 'layouts');
	count(c.charts, 'chart', 'charts');
	count(c.dashboards, 'dashboard', 'dashboards');
	if (!parts.length) return '';
	return `It also adds ${inWords(parts)} for its cards. Nothing of yours is changed: a name you already use gets the package's name after it.`;
}

/**
 * After a card or bundle package was added (keepr-api KPR-338): what was
 * renamed because the name was already the person's, and whether their
 * Overview kept its own dashboard. What was left out arrives as `warnings`,
 * which the dialog already shows. '' when there is nothing to say.
 */
export function installedSettingsNote(settings) {
	if (!settings) return '';
	const renamed = (Array.isArray(settings.added) ? settings.added : []).filter(a => a && a.renamedFrom);
	const parts = [];
	if (renamed.length === 1) {
		parts.push(`You already had "${renamed[0].renamedFrom}", so the package's is "${renamed[0].name}".`);
	} else if (renamed.length > 1) {
		const shown = renamed.slice(0, 3).map(a => `"${a.name}"`);
		parts.push(`${renamed.length} of its names were already yours, so it added them with its name after them: ${inWords(renamed.length > 3 ? [...shown, 'more'] : shown)}.`);
	}
	if (settings.overview && settings.overview.set === false) parts.push('Your Overview still shows your own dashboard.');
	return parts.join(' ');
}

const PREVIEW_KINDS = {
	savedFilter: { one: 'saved filter', many: 'saved filters' },
	quickAddTemplate: { one: 'quick add', many: 'quick adds' },
	multiAddForm: { one: 'multi add form', many: 'multi add forms' },
	cardLayout: { one: 'layout', many: 'layouts' },
	chart: { one: 'chart', many: 'charts' },
	dashboard: { one: 'dashboard', many: 'dashboards' },
};
// Kinds the dialog names but does not count among what the pack adds "for its
// cards": a collection's own vocabulary (KPR-443) and its tag rules.
const NAMED_KINDS = {
	tag: { one: 'tag', many: 'tags' },
	tagRule: { one: 'tag rule', many: 'tag rules' },
};
const nounOf = kind => PREVIEW_KINDS[kind]?.one || NAMED_KINDS[kind]?.one || kind;
const capitalized = text => text.charAt(0).toUpperCase() + text.slice(1);
const countWords = (n, one, many) => (n === 1 ? `a ${one}` : `${n} ${many}`);

/** A left-out row's label, in the product's words (KPR-666): never the raw kind. */
function leftOutLabel(x) {
	if (x.kind === 'cardEntry') {
		const what = x.setting === 'favorite' ? 'favorite' : x.setting === 'itemTags' ? 'item tags' : 'card settings';
		return `${x.name} (${what})`;
	}
	return `${capitalized(nounOf(x.kind))} "${x.name}"`;
}

/**
 * What the install dialog tells a person about the collection they chose,
 * before they press Add (keepr-api KPR-445). `preview` is `previewPackageInstall`'s
 * answer; null for anything that is not one (an API from before the preview,
 * an answer of another shape): the dialog then says nothing it cannot back.
 *
 * → `{ refusal, keys, adds, reused, renamed: [{ noun, from, to, restricted? }], leftOut: [{ label,
 * reason }], overview, clear }` — plain words and the server's own
 * reasons, the same ones the install's answer gives afterwards (the two are
 * compared by keepr-api's tests, so they cannot disagree). `refusal` is the
 * install's refusal as data (the dialog words it as it words the install's);
 * `clear` is true when nothing in the collection is in the way.
 */
export function installPreviewNotes(preview) {
	if (!preview || typeof preview !== 'object' || typeof preview.installable !== 'boolean') return null;
	const settings = preview.settings && typeof preview.settings === 'object' ? preview.settings : null;
	const added = Array.isArray(settings?.added) ? settings.added.filter(a => a && a.kind) : [];
	const leftOutRows = Array.isArray(settings?.leftOut) ? settings.leftOut.filter(x => x && x.reason) : [];
	const adjusted = Array.isArray(preview.keyAdjusted) ? preview.keyAdjusted.filter(a => a && a.requested && a.installed) : [];

	const parts = [];
	Object.keys(PREVIEW_KINDS).forEach((kind) => {
		const n = added.filter(a => a.kind === kind).length;
		if (n) parts.push(n === 1 ? `a ${PREVIEW_KINDS[kind].one}` : `${n} ${PREVIEW_KINDS[kind].many}`);
	});
	const renamed = added.filter(a => a.renamedFrom).map(a => ({
		noun: a.kind === 'cardLayout' ? 'page layout' : nounOf(a.kind),
		from: a.renamedFrom,
		to: a.name,
		...(a.restricted ? { restricted: true } : {}),
	}));
	const leftOut = leftOutRows.map(x => ({ label: leftOutLabel(x), reason: x.reason }));

	// A pack's tags, tag rules and its cards' favorite / item-tag settings (KPR-443, KPR-444):
	// a tag the collection already had is the person's, said apart and not counted as added.
	const extra = [];
	const favorites = added.filter(a => a.kind === 'cardEntry' && a.favorite).length;
	const itemTags = added.filter(a => a.kind === 'cardEntry' && a.itemTags).length;
	const newTags = added.filter(a => a.kind === 'tag' && !a.reused).length;
	const tagRules = added.filter(a => a.kind === 'tagRule').length;
	if (favorites) extra.push(countWords(favorites, 'favorite', 'favorites'));
	if (itemTags) extra.push(`item tags on ${countWords(itemTags, 'card', 'cards')}`);
	if (newTags) extra.push(countWords(newTags, NAMED_KINDS.tag.one, NAMED_KINDS.tag.many));
	if (tagRules) extra.push(countWords(tagRules, NAMED_KINDS.tagRule.one, NAMED_KINDS.tagRule.many));
	const reusedTags = added.filter(a => a.kind === 'tag' && a.reused);
	let reused = '';
	if (reusedTags.length === 1) reused = `It will use your tag ${reusedTags[0].name} as it is.`;
	else if (reusedTags.length > 1) reused = `It will use ${reusedTags.length} of your tags as they are: ${inWords(reusedTags.slice(0, 3).map(a => a.name).concat(reusedTags.length > 3 ? ['more'] : []))}.`;

	let overview = '';
	if (settings?.overview && settings.overview.set === false) overview = 'Your Overview will keep showing your own dashboard.';
	else if (settings?.overview && settings.overview.set === true) overview = 'Its dashboard will be the Overview, since that collection shows none.';

	// Future tense of keyAdjustedNote: the same keys, said before they happen.
	let keys = '';
	if (adjusted.length) {
		const noun = a => (a.kind === 'elementSet' ? `the element set ${a.requested}` : `the card ${a.requested}`);
		keys = adjusted.length === 1
			? `That collection already has ${noun(adjusted[0])}, so yours will be kept as it is and the package's added as ${adjusted[0].installed}.`
			: `That collection already has ${inWords(adjusted.map(noun))}, so yours will be kept as they are and the package's added as ${inWords(adjusted.map(a => a.installed))}.`;
	}
	const refusal = preview.installable ? null : (preview.refusal && typeof preview.refusal === 'object' ? preview.refusal : { code: null });
	return {
		refusal,
		keys,
		adds: [
			parts.length ? `It will add ${inWords(parts)} for its cards.` : '',
			extra.length ? `It will ${parts.length ? 'also ' : ''}add ${inWords(extra)}.${tagRules ? (tagRules === 1 ? ' Its tag rule will be paused.' : ' Its tag rules will be paused.') : ''}` : '',
		].filter(Boolean).join(' '),
		reused,
		renamed,
		leftOut,
		overview,
		// KPR-1065 (keepr-api KPR-1064): a package whose examples are dated from
		// the install day answers `exampleDates`, only when previewed with examples.
		exampleDates: Boolean(preview.exampleDates && typeof preview.exampleDates === 'object'),
		clear: !refusal && !keys && !renamed.length && !leftOut.length && settings?.overview?.set !== false,
	};
}

const quoted = names => inWords(names.map(n => `"${n}"`));
/** A layout an add-on adds to, in words: the "Vehicle" table, the "Receipt" page of "Vehicle". */
const layoutWhere = x => (x.layout === 'page' ? `the "${x.layoutName || 'page'}" page of "${x.card}"` : `the "${x.card}" ${x.layout === 'tile' ? 'tile' : 'table'}`);

/**
 * What the dialog tells a person about adding add-ons to a collection that
 * already holds the bundle, before they press Add (keepr-api KPR-632).
 * `preview` is `previewPackageAddOns`' answer; null for anything that is not
 * one (an API from before it): the dialog then says nothing it cannot back.
 * Every line is in words — card, chart, list and element names as the
 * person reads them, never an id or a key.
 *
 * → { refusal, cards, places: [line], leftOut: [{ label, reason }], settings,
 *     examples, clear }
 */
export function addOnPreviewNotes(preview) {
	if (!preview || typeof preview !== 'object' || typeof preview.installable !== 'boolean') return null;
	if (!preview.installable) return { refusal: preview.refusal && typeof preview.refusal === 'object' ? preview.refusal : { code: null }, cards: '', places: [], leftOut: [], settings: '', examples: '', clear: false };
	const core = preview.core && typeof preview.core === 'object' ? preview.core : {};
	const added = Array.isArray(core.added) ? core.added.filter(x => x && x.kind) : [];
	const cards = (Array.isArray(preview.cards) ? preview.cards : []).map(c => c && c.name).filter(Boolean);
	// A card added beside one of the person's that has its key (KPR-357): said
	// by the card's name — the key is not a word the person reads.
	const movedKeys = new Set((Array.isArray(preview.keyAdjusted) ? preview.keyAdjusted : []).filter(a => a && a.kind === 'card').map(a => a.installed));
	const beside = (Array.isArray(preview.cards) ? preview.cards : []).filter(c => c && c.name && movedKeys.has(c.key)).map(c => c.name);
	const cardsLine = [
		cards.length ? `Adds ${cards.length === 1 ? 'the card' : 'the cards'} ${quoted(cards)}.` : '',
		beside.length ? `${beside.length === 1 ? `A card of yours already uses the key of ${quoted(beside)}` : `Cards of yours already use the keys of ${quoted(beside)}`}: yours stay as they are, and the add-on's are added beside them.` : '',
	].filter(Boolean).join(' ');

	// Grouped by where each goes, in the order the answer lists them.
	const groups = new Map();
	const into = (key, make) => { if (!groups.has(key)) groups.set(key, make()); return groups.get(key); };
	added.forEach((x) => {
		if (x.kind === 'element') into(`element:${x.card}`, () => ({ kind: 'element', where: x.card, names: [] })).names.push(x.label || x.element);
		else if (x.kind === 'tile') into(`tile:${x.dashboard}`, () => ({ kind: 'tile', where: x.dashboard, names: [] })).names.push(x.chart);
		else if (x.kind === 'relatedList') into(`list:${x.card}`, () => ({ kind: 'relatedList', where: x.card, names: [] })).names.push(x.list);
		else if (x.kind === 'formPart') into(`form:${x.card}:${x.part}`, () => ({ kind: x.part === 'leftOut' ? 'formLeftOut' : 'formPlaced', where: x.card, names: [] })).names.push(x.label || x.element);
		// keepr-api KPR-1329: a table's column or sort, a tile's placement, a page's block.
		// keepr-api KPR-1412: a block naming only a card or set, said as that one's block.
		else if (x.kind === 'layoutPart' && x.part === 'block' && x.named) into(`layout:${x.layout}:${x.card}:${x.layoutName || ''}:cardBlock`, () => ({ kind: 'layout', part: 'cardBlock', where: layoutWhere(x), names: [] })).names.push(x.label || x.element);
		else if (x.kind === 'layoutPart') into(`layout:${x.layout}:${x.card}:${x.layoutName || ''}:${x.part}`, () => ({ kind: 'layout', part: x.part, where: layoutWhere(x), names: [] })).names.push(x.label || x.element);
		// keepr-api KPR-1388: a quick add's default, a multi add's step or field.
		else if (x.kind === 'quickAddPart') into(`quick:${x.quickAdd}:${x.part}`, () => ({ kind: 'quick', part: x.part, where: `the "${x.quickAdd}" quick add`, names: [] })).names.push(x.section || x.label || x.element);
		else if (x.kind === 'multiAddPart') into(`multi:${x.multiAdd}:${x.part}`, () => ({ kind: 'multi', part: x.part, where: `the "${x.multiAdd}" multi add`, names: [] })).names.push(x.label || x.element || x.step);
	});
	const places = [...groups.values()].map((g) => {
		const n = g.names.length;
		if (g.kind === 'element') return `Adds ${n === 1 ? 'an element' : `${n} elements`} to "${g.where}": ${inWords(g.names)}.`;
		if (g.kind === 'tile') return `Adds ${n === 1 ? 'the chart' : 'the charts'} ${quoted(g.names)} to the dashboard "${g.where}".`;
		if (g.kind === 'relatedList') return `Adds ${n === 1 ? 'the list' : 'the lists'} ${quoted(g.names)} to "${g.where}".`;
		if (g.kind === 'layout') {
			if (g.part === 'sort') return `Sorts ${g.where} by ${inWords(g.names)}.`;
			if (g.part === 'cardBlock') return `Adds ${n === 1 ? 'a block' : `${n} blocks`} of ${quoted([...new Set(g.names)])} to ${g.where}.`;
			const noun = { column: ['a column', 'columns'], placement: ['a field', 'fields'], block: ['a block', 'blocks'] }[g.part] || ['a part', 'parts'];
			return `Adds ${n === 1 ? noun[0] : `${n} ${noun[1]}`} to ${g.where}: ${inWords(g.names)}.`;
		}
		if (g.kind === 'quick') {
			if (g.part === 'lastItemMatch') return `Matches the last item on ${inWords(g.names)} in ${g.where}.`;
			// keepr-api KPR-1389: a claimed preset list section.
			if (g.part === 'presetSection') return `Adds ${n === 1 ? 'the section' : 'the sections'} ${quoted(g.names)} to the list ${g.where} opens with.`;
			return `Adds ${n === 1 ? 'a default' : `${n} defaults`} to ${g.where}: ${inWords(g.names)}.`;
		}
		if (g.kind === 'multi') return `Adds ${g.part === 'step' ? (n === 1 ? 'a step' : `${n} steps`) : (n === 1 ? 'a field' : `${n} fields`)} to ${g.where}: ${inWords(g.names)}.`;
		if (g.kind === 'formLeftOut') return `Keeps ${n === 1 ? 'a field' : `${n} fields`} off the "${g.where}" form: ${inWords(g.names)}.`;
		return `Places ${n === 1 ? 'a field' : `${n} fields`} on the "${g.where}" form: ${inWords(g.names)}.`;
	});

	const coreLeft = (Array.isArray(core.leftOut) ? core.leftOut : []).filter(x => x && x.reason).map((x) => {
		let label;
		if (x.kind === 'tile') label = `The chart "${x.chart}" on "${x.dashboard}"`;
		else if (x.kind === 'relatedList') label = `The list "${x.list}" on "${x.card}"`;
		else if (x.kind === 'formPart') label = `"${x.label || x.element}" on the "${x.card}" form`;
		else if (x.kind === 'layoutPart' && x.part === 'block' && x.named) label = `A "${x.label || x.element}" block on ${layoutWhere(x)}`;
		else if (x.kind === 'layoutPart') label = `"${x.label || x.element}" on ${layoutWhere(x)}`;
		// keepr-api KPR-1388.
		else if (x.kind === 'quickAddPart' && x.part === 'presetSection') label = `The "${x.section || x.label}" section of the "${x.quickAdd}" quick add`;
		else if (x.kind === 'quickAddPart') label = x.part === 'lastItemMatch' ? `Matching on "${x.label || x.element}" in the "${x.quickAdd}" quick add` : `The "${x.label || x.element}" default of the "${x.quickAdd}" quick add`;
		else if (x.kind === 'multiAddPart') label = x.part === 'step' ? `The "${x.step}" step of the "${x.multiAdd}" multi add` : `"${x.label || x.element}" in the "${x.multiAdd}" multi add`;
		else label = `"${x.label || x.element}" on "${x.card}"`;
		return { label, reason: x.reason };
	});
	// The add-ons' own settings, as the install preview words them.
	const settingNotes = installPreviewNotes({ installable: true, settings: preview.settings, keyAdjusted: [] });
	const settingsLeft = settingNotes ? settingNotes.leftOut : [];
	const settingsLine = settingNotes ? [settingNotes.adds, ...(settingNotes.renamed.length ? [settingNotes.renamed.length === 1
		? `One of its names is already yours, so the ${settingNotes.renamed[0].noun} "${settingNotes.renamed[0].from}" is added as "${settingNotes.renamed[0].to}".`
		: `${settingNotes.renamed.length} of its names are already yours, so the add-on's are added with its name after them.`] : [])].filter(Boolean).join(' ') : '';

	let examples = '';
	if (preview.examples && typeof preview.examples === 'object') {
		const { count = 0, leftOut = 0 } = preview.examples;
		if (count) examples = `Brings ${count === 1 ? 'an example' : `${count} examples`}.`;
		if (leftOut) examples = `${examples ? `${examples} ` : ''}${leftOut === 1 ? 'One example is' : `${leftOut} examples are`} left out: ${leftOut === 1 ? 'it needs an example' : 'they need examples'} that ${leftOut === 1 ? 'is' : 'are'} not in this collection.`;
	}
	const leftOut = [...coreLeft, ...settingsLeft];
	return {
		refusal: null,
		cards: cardsLine,
		places,
		leftOut,
		settings: settingsLine,
		examples,
		clear: !leftOut.length,
	};
}

export { PAUSED_RULES_SHOWN, pausedRulesOf, packOwnRuleIds, pausedYourRulesWords } from '../utils/pausedRules';

/**
 * What the install answer's `keyAdjusted` (keepr-api KPR-357) tells the
 * person: the collection already had a card or element set under one of the
 * package's keys, theirs was kept as it is, and the package's was added under
 * the suffixed key. '' when nothing was adjusted. Keys, not names: the two
 * cards may well share a name, and the key is what tells them apart.
 */
export function keyAdjustedNote(adjusted) {
	const list = Array.isArray(adjusted) ? adjusted.filter(a => a && a.requested && a.installed) : [];
	if (!list.length) return '';
	const noun = a => (a.kind === 'elementSet' ? `the element set ${a.requested}` : `the card ${a.requested}`);
	const join = parts => (parts.length < 2 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`);
	if (list.length === 1) {
		const [a] = list;
		return `This collection already had ${noun(a)}, so yours was kept as it is and the package's was added as ${a.installed}.`;
	}
	return `This collection already had ${join(list.map(noun))}, so yours were kept as they are and the package's were added as ${join(list.map(a => a.installed))}.`;
}

/**
 * Where an item lookup on a package's card points, in words: one of its own
 * cards, a global card, or a card of a published global collection
 * (keepr-api KPR-384) — "Product in Medications", and when that collection is
 * going away what adding the package does: the item lookup arrives empty.
 * `byLocal` maps the package's own cards by local id.
 */
export function lookupTargetWords(lookup, byLocal) {
	if (!lookup) return '';
	if (lookup.ref) return byLocal?.get(lookup.ref)?.name || 'a card here';
	if (lookup.published) {
		const where = lookup.collection ? `${lookup.name || 'a card'} in ${lookup.collection}` : (lookup.name || 'a card');
		if (lookup.state === 'retiring') return `${where}, which is retiring: it arrives empty`;
		if (lookup.state === 'unavailable') return `${where}, no longer published: it arrives empty`;
		return where;
	}
	return lookup.name || lookup.globalKey || '';
}

export const PACKAGE_KIND_LABELS = { card: 'Card', bundle: 'Card bundle', collection: 'Collection' };

// ---- admin (the authoring surface; keepr-api /api/admin/packages) ----

const API_ADMIN_PACKAGES_URL = API_PACKAGES_URL.replace(/\/packages$/, '/admin/packages');

/** `{ rows, total }` for every package, any status. `q` is KQL over the packages subject (status included). */
export async function listAdminPackages({ q, skip = 0, limit = 50 } = {}) {
	const params = { skip, limit };
	if (q) params.q = q;
	const { data, headers } = await api.get(API_ADMIN_PACKAGES_URL, { params });
	const rows = Array.isArray(data) ? data : [];
	return { rows, total: readTotalCount(headers, rows.length) };
}

/** Build a DRAFT package from any collection: `{ key, kind, name?, category?, cardIds?, includeItems?, itemsRole?, addOns?, listings?, exampleSets? }` (add-ons and listings: a bundle's, keepr-api KPR-383; a card or bundle's items are its examples, and a bundle's example sets, KPR-465). */
export async function createPackageFromCollection(collectionId, body) {
	const { data } = await api.post(`${API_ADMIN_PACKAGES_URL}/from-collection/${collectionId}`, body);
	return data;
}

/** The listing fields, `itemsRole` and a bundle's `listings` (keepr-api KPR-383; each listing's `examples`, KPR-465). */
export async function patchPackage(packageId, body) {
	const { data } = await api.patch(`${API_ADMIN_PACKAGES_URL}/${packageId}`, body);
	return data;
}

const lifecycle = verb => async packageId => {
	const { data } = await api.post(`${API_ADMIN_PACKAGES_URL}/${packageId}/${verb}`);
	return data;
};
export const publishPackage = lifecycle('publish');
export const retirePackage = lifecycle('retire');

/**
 * Re-snapshot from the source collection. `body` (keepr-api KPR-383), when
 * given, replaces a bundle's add-ons and listings in the same call, and turns
 * a card or bundle's examples on or off (KPR-465):
 * `{ addOns?, listings?, includeItems?, exampleSets? }`.
 */
export async function rebuildPackage(packageId, body) {
	const url = `${API_ADMIN_PACKAGES_URL}/${packageId}/rebuild`;
	const { data } = await (body ? api.post(url, body) : api.post(url));
	return data;
}

// ---- the bundles installed in a collection (keepr-api KPR-1130) ----

/**
 * The card and bundle packages a collection holds, from the API's install
 * record (GET /api/collections/{id}/packages; collection-level read):
 * `[{ key, name, publisher, kind, version, publishedVersion, retired,
 * addOns: { installed: [{ key, name, cardIds }], notInstalled? },
 * cardIds, elementSetIds, dashboardIds, updateAvailable? }]`. A manager alone
 * is told `updateAvailable` and `addOns.notInstalled`; for a reader the keys
 * are absent. Errors propagate: a 403 or 404 (an API older than the endpoint,
 * a grant that reaches only some cards) is the caller's to read as "no groups".
 */
export async function getCollectionPackages(collectionId) {
	const { data } = await api.get(`${API_COLLECTIONS_URL}/${encodeURIComponent(collectionId)}/packages`);
	return Array.isArray(data) ? data : [];
}

// ---- removing a bundle (keepr-api KPR-1131) ----

const bundleUrl = (collectionId, key) => `${API_COLLECTIONS_URL}/${encodeURIComponent(collectionId)}/packages/${encodeURIComponent(key)}`;

/**
 * What removing a bundle from a collection would take (POST …/remove/preview;
 * manage on the collection; writes nothing): `{ collectionId, package: { id,
 * key, name, kind, version, addOns }, goes: { <kind>: [{ id, name, … }] },
 * stays: [{ kind, reason, … }], blockers: [{ code, … }], previewToken }`. A
 * `goes.cards` row carries `items`, how many live records go with it. Errors
 * propagate (404 `package_not_installed`, 409 `package_install_in_progress`).
 */
export async function previewBundleRemoval(collectionId, key) {
	const { data } = await api.post(`${bundleUrl(collectionId, key)}/remove/preview`, {});
	return data;
}

/**
 * Removes the bundle the preview described (POST …/remove, `{ previewToken }`)
 * → `{ collectionId, package, removed, stays, record }`. Refusals propagate:
 * 409 `remove_blocked` (`blockers`), `remove_preview_stale`, `remove_in_progress`,
 * 503 `history_unavailable`; a failure part-way carries `partial: { removed,
 * left, failed }` in the response body.
 */
export async function removeBundle(collectionId, key, previewToken) {
	const { data } = await api.post(`${bundleUrl(collectionId, key)}/remove`, { previewToken });
	return data;
}

/**
 * Keeps the bundle's cards as the collection's own and stops tracking the
 * bundle (POST /api/collections/{id}/packages/{key}/detach; keepr-api KPR-1133,
 * managers only) → `{ collectionId, package, detached: { cards, elementSets,
 * dashboards }, record }`. Nothing is deleted and no record is changed; the
 * bundle can be installed again beside the cards. Refusals propagate:
 * 404 `package_not_installed`, 409 `package_install_in_progress`,
 * `remove_in_progress`, `detach_taken_over`, 503 `history_unavailable`.
 */
export async function detachBundle(collectionId, key) {
	const { data } = await api.post(`${API_COLLECTIONS_URL}/${encodeURIComponent(collectionId)}/packages/${encodeURIComponent(key)}/detach`, {});
	return data;
}

// ---- example data added later (keepr-api KPR-365) ----

/**
 * "Add example data": a package's examples into a collection it is already in
 * (POST /api/collections/{id}/packages/{key}/examples; managers only, no body)
 * → `{ collectionId, itemCount, alreadyHere, exampleDates?, warnings }`. They
 * land on the cards the install made there, marked demo, so *Remove demo data*
 * takes them out again. A second press adds nothing (`itemCount: 0`).
 * Refusals propagate: 409 `package_no_examples`, `package_not_installed`,
 * `examples_cards_missing`, `plan_limit`.
 */
export async function addExampleData(collectionId, key) {
	const { data } = await api.post(`${bundleUrl(collectionId, key)}/examples`, {});
	return data;
}

/** What an add of example data did, in one line: "Added 7 example records." / "The examples are already here." */
export function exampleDataAddedNote(answer) {
	const n = Number(answer?.itemCount) || 0;
	if (n > 0) return n === 1 ? 'Added 1 example record.' : `Added ${n} example records.`;
	if (Number(answer?.alreadyHere) > 0) return 'The examples are already here.';
	return 'No example records were added.';
}

/** The menu entry's label for one package of `exampleData`, `several` when the collection holds more than one. */
export function addExampleDataLabel(entry, several = false) {
	const n = Number(entry?.items) || 0;
	return several ? `Add example data from ${entry?.name || 'a package'} (${n})…` : `Add example data (${n})…`;
}

// ---- taking a bundle's later version, change by change (keepr-api KPR-458) ----

/**
 * The proposal for taking a bundle's published version (GET
 * /api/collections/{id}/packages/{key}/update; managers, in a session; writes
 * nothing): `{ collectionId, package, version, publishedVersion, upToDate,
 * ignored, basis, previewToken, counts, changes: [{ id, kind, op, field?,
 * column?, keys?, addOn, target, summary, yours?, theirs?, conflict,
 * conflictWords?, basis, state, applicable, code?, why?, recomputes?, paused?,
 * loosens?, holdsData?, values?, records?, revives?, keeps?, needs }], notOffered }` — a repeating
 * group's changes are `kind: 'group'`, each naming the group and its `column`
 * (KPR-696). Errors propagate (404
 * `package_not_installed`, 409 `package_not_published`, …).
 */
export async function getBundleUpdate(collectionId, key) {
	const { data } = await api.get(`${bundleUrl(collectionId, key)}/update`);
	return data;
}

/**
 * Takes what the person chose (POST …/update): `{ previewToken, accept,
 * decline }`, or `{ previewToken, acceptAll: true }` → `{ collectionId,
 * package, version, publishedVersion, upToDate, applied, declined, failed,
 * left }`. Refusals propagate: 409 `update_preview_stale` (read the proposal
 * again), `update_in_progress`, `update_not_available`; a failure part-way
 * carries `partial: { applied, declined, failed, left }` in the body.
 */
export async function takeBundleUpdate(collectionId, key, body) {
	const { data } = await api.post(`${bundleUrl(collectionId, key)}/update`, body);
	return data;
}

/** "Ignore this update" (POST …/update/ignore `{ version }`) → `{ ignoredVersion }`: Update available stays off until a newer version. */
export async function ignoreBundleUpdate(collectionId, key, version) {
	const { data } = await api.post(`${bundleUrl(collectionId, key)}/update/ignore`, { version });
	return data;
}

/** Is this install behind the published version, so a manager may review an update (the chip may be off: an ignored version)? */
export function canReviewUpdate({ version, publishedVersion, retired } = {}) {
	const have = Number(version);
	const now = Number(publishedVersion);
	return !retired && Number.isFinite(have) && Number.isFinite(now) && have < now;
}

// The groups the proposal is read in, in order.
const UPDATE_GROUPS = [
	{ key: 'cards', label: 'Cards and their elements', kinds: [ 'card', 'element', 'group' ] },
	{ key: 'settings', label: 'Views, launchers and charts', kinds: [ 'savedFilter', 'quickAddTemplate', 'multiAddForm', 'cardLayout', 'chart', 'dashboard' ] },
	{ key: 'rules', label: 'Rules and reminders', kinds: [ 'automation', 'notification', 'elementRule' ] },
];

/** The proposal's changes in the dialog's groups, each `{ key, label, changes }`; a group with none is left out. */
export function updateGroups(changes) {
	const list = Array.isArray(changes) ? changes : [];
	return UPDATE_GROUPS
		.map(group => ({ key: group.key, label: group.label, changes: list.filter(c => group.kinds.includes(c.kind)) }))
		.filter(group => group.changes.length);
}

/** Can Accept all take this change on its own (clean, takeable, not a recompute, a `loosens` rule or a removal that `holdsData`: KPR-1710, KPR-1421)? */
export function takenByAcceptAll(change) {
	return change?.state === 'proposed' && change.applicable !== false && !change.conflict && !change.recomputes && !change.loosens && !change.holdsData;
}

/**
 * What Accept all does, in one sentence: how many it takes, and how many stay
 * for the person to choose (conflicts, recomputes) or stay theirs (cannot be
 * taken here).
 */
export function acceptAllNote(changes) {
	const open = (Array.isArray(changes) ? changes : []).filter(c => c.state === 'proposed');
	const takes = open.filter(takenByAcceptAll).length;
	const choose = open.filter(c => c.applicable !== false && (c.conflict || c.recomputes || c.loosens || c.holdsData)).length;
	const cannot = open.filter(c => c.applicable === false).length;
	const parts = [ `Accept all takes ${takes} ${takes === 1 ? 'change' : 'changes'}` ];
	if (choose) parts.push(`${choose} ${choose === 1 ? 'needs' : 'need'} your choice`);
	if (cannot) parts.push(`${cannot} cannot be taken here and ${cannot === 1 ? 'stays' : 'stay'} as ${cannot === 1 ? 'it is' : 'they are'}`);
	return `${parts.join('; ')}.`;
}

/**
 * A recompute (keepr-api KPR-1057) that reads something the same update adds
 * (`recompute.waiting`): it is neither previewed nor taken until that change
 * is in, so its row cannot be set to Take.
 */
export function recomputeWaiting(change) {
	return Boolean(change?.recomputes && change?.recompute?.waiting);
}

/**
 * What a recompute's row says about the values it would replace (keepr-api
 * KPR-1057: an element's calculation worked out another way carries the card
 * editor's own preview, `recompute`). '' when the server sent none (a group
 * column's recompute: its summary counts the records).
 */
export function recomputeLine(change) {
	const r = change?.recompute;
	if (!change?.recomputes || !r) return '';
	const n = Number.isFinite(r.withValues) ? r.withValues : null;
	const held = n === null
		? 'keepr could not count the records holding a value in it'
		: (n === 0 ? 'No record holds a value in it yet' : `${n.toLocaleString()} ${n === 1 ? 'record holds' : 'records hold'} a value in it today`);
	if (r.waiting) return `${held}.`;
	if (r.sampleReason === 'hidden') return `${held}. It reads something hidden from you, so no values are shown before you take it.`;
	if (!n) return `${held}.`;
	if (r.unavailable) return `${held}. keepr could not work out what they would become right now.`;
	if (r.incomplete) return `${held}. Before you take it you see some of them now and after; keepr stopped checking early, so it is only a partial sample.`;
	return `${held}. Before you take it you see some of them now and after.`;
}

/** A not-offered entry of the proposal in words (a group copied from the bundle's starter, KPR-696). */
export function notOfferedWords(entry) {
	if (entry?.kind === 'repeatingGroupCopy') return `the group “${entry.element}” copied onto “${entry.card}”`;
	return `“${entry?.element}” on “${entry?.card}”`;
}

/**
 * What a take did, in one line. After Accept all (`acceptAll`), what it kept
 * as the person's because it cannot be taken here is said too (KPR-1057: a
 * typed element made calculated, among others).
 */
export function updateTakenNote(answer, name = 'the bundle') {
	const applied = (answer?.applied || []).length;
	const failed = (answer?.failed || []).length;
	const kept = answer?.acceptAll ? (answer?.declined || []).length : 0;
	const keptWords = kept ? ` ${kept} could not be taken here and ${kept === 1 ? 'is' : 'are'} kept as yours.` : '';
	const took = `Took ${applied} ${applied === 1 ? 'change' : 'changes'}`;
	if (answer?.upToDate) return applied ? `${took}. ${name} is up to date.${keptWords}` : `${name} is up to date.${keptWords}`;
	if (!applied && !failed) return kept ? keptWords.trim() : 'Your choices are saved.';
	return failed ? `${took}; ${failed} could not be taken.${keptWords}` : `${took}.${keptWords}`;
}
